import { useQuery } from "@apollo/client/react"
import { ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"
import { gql } from "~/__generated__"
import { useViewer } from "~/auth/use-viewer"
import { puzzleLabelPath, registerPuzzlePath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { Badge } from "~/ui/badge"
import { Button } from "~/ui/button"
import { Card, CardContent } from "~/ui/card"
import { GraphqlError } from "~/ui/graphql-error"
import { ScreenLoading } from "~/ui/screen-loading"

const dashboardQuery = gql(/* GraphQL */ `
  query Dashboard {
    viewer {
      id
      firstName
      puzzlesLoggedCount
      puzzlesRegisteredCount
      totalPiecesLogged
      myPuzzles {
        id
        wpId
        title
        brand
        pieceCount
        homeCity
        homeState
        photoThumbUrl
        journeyLogCount
        viewerIsOwner
        viewerHasLogged
        journeyLogs(first: 3) {
          nodes {
            id
            completedOn
            city
            state
          }
        }
      }
    }
  }
`)

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })

const StatTile = ({ label, value }: { label: string; value: number | string }) => (
  <div className="rounded-lg border bg-white p-4 shadow-sm">
    <div className="text-2xl font-semibold">{value}</div>
    <div className="mt-1 text-xs uppercase tracking-wider text-gray-500">{label}</div>
  </div>
)

const RelationshipBadge = ({ isOwner, hasLogged }: { isOwner: boolean; hasLogged: boolean }) => {
  if (isOwner && hasLogged) {
    return <Badge variant="secondary">Registered & logged</Badge>
  }
  if (isOwner) {
    return <Badge variant="secondary">Registered by you</Badge>
  }
  if (hasLogged) {
    return <Badge variant="secondary">Logged</Badge>
  }
  return null
}

const StopChip = ({
  city,
  state,
  completedOn,
}: {
  city: string | null | undefined
  state: string | null | undefined
  completedOn: string
}) => {
  const where = [city, state].filter(Boolean).join(", ") || "Somewhere"
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs text-stone-700">
      <span className="font-medium">{where}</span>
      <span className="text-stone-400">·</span>
      <span>{formatDate(completedOn)}</span>
    </span>
  )
}

export const DashboardScreen = () => {
  useDocumentTitle("Dashboard")
  const { viewer } = useViewer()
  const { data, loading, error } = useQuery(dashboardQuery)

  if (loading && !data) return <ScreenLoading />
  if (error) return <GraphqlError error={error} />

  const v = data?.viewer
  if (!v) return null

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-6 sm:py-10">
      <header>
        <p className="text-xs uppercase tracking-wider text-gray-500">Dashboard</p>
        <h1 className="font-display text-3xl font-semibold sm:text-4xl">Hi, {viewer.firstName}.</h1>
      </header>

      <section className="mt-8 grid grid-cols-3 gap-3">
        <StatTile label="Puzzles logged" value={v.puzzlesLoggedCount} />
        <StatTile label="Puzzles registered" value={v.puzzlesRegisteredCount} />
        <StatTile label="Pieces logged" value={v.totalPiecesLogged.toLocaleString()} />
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-semibold">My puzzles</h2>
        <p className="mt-1 text-sm text-stone-600">
          Puzzles you&apos;ve registered or logged a journey on, and where they&apos;ve been since.
        </p>

        {v.myPuzzles.length === 0 ? (
          <Card className="mt-6">
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <h3 className="font-medium">No puzzles yet</h3>
              <p className="max-w-sm text-sm text-gray-600">
                Register a puzzle to print a sticker and start its journey, or scan a sticker on a
                puzzle you finished to log it.
              </p>
              <Button asChild className="mt-1">
                <Link to={registerPuzzlePath({})}>Register a puzzle</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {v.myPuzzles.map((puzzle) => {
              const stops = puzzle.journeyLogs.nodes
              return (
                <li key={puzzle.id}>
                  <Card>
                    <CardContent className="flex gap-3 py-4">
                      <Link to={`/p/${puzzle.wpId}`} className="shrink-0">
                        {puzzle.photoThumbUrl ? (
                          <img
                            src={puzzle.photoThumbUrl}
                            alt={puzzle.title}
                            className="h-16 w-16 rounded bg-stone-100 object-contain"
                          />
                        ) : (
                          <div className="h-16 w-16 rounded bg-gray-100" />
                        )}
                      </Link>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link
                            to={`/p/${puzzle.wpId}`}
                            className="truncate font-medium hover:underline"
                          >
                            {puzzle.title}
                          </Link>
                          <Badge variant="secondary" className="font-mono text-[10px]">
                            {puzzle.wpId}
                          </Badge>
                          <RelationshipBadge
                            isOwner={puzzle.viewerIsOwner}
                            hasLogged={puzzle.viewerHasLogged}
                          />
                        </div>
                        <div className="text-xs text-gray-500">
                          {puzzle.brand} · {puzzle.pieceCount.toLocaleString()} pieces
                        </div>
                        {stops.length > 0 ? (
                          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                            {stops.map((stop) => (
                              <StopChip
                                key={stop.id}
                                city={stop.city}
                                state={stop.state}
                                completedOn={stop.completedOn}
                              />
                            ))}
                          </div>
                        ) : (
                          <p className="mt-3 text-xs text-stone-500">No stops logged yet.</p>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                          <Link
                            to={`/p/${puzzle.wpId}`}
                            className="inline-flex items-center gap-1 text-stone-700 hover:text-stone-900"
                          >
                            View journey <ArrowRight size={14} aria-hidden />
                          </Link>
                          {puzzle.viewerIsOwner ? (
                            <Link
                              to={puzzleLabelPath({ wpId: puzzle.wpId })}
                              className="text-stone-500 hover:text-stone-800"
                            >
                              Get sticker
                            </Link>
                          ) : null}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </main>
  )
}
